import React, { useState, useEffect, useCallback } from 'react';
import {
  getGuideSections, createGuideSection, updateGuideSection, deleteGuideSection,
  getLegalDocuments, createLegalDocument, updateLegalDocument, deleteLegalDocument,
  getAboutInfo, updateAboutInfo
} from '../services/adminService';
import { pageContainer, cardStyle, thStyle, tdStyle, badgeStyle, primaryBtn, secondaryBtn, inputStyle } from '../styles/theme';
import { useToast } from '../utils/useAdminPage';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', zIndex: 1000 };
const modal: React.CSSProperties = { ...cardStyle, width: 540, maxHeight: '85vh', overflow: 'auto' };
const row: React.CSSProperties = { marginBottom: 14 };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: 4 };
const toggle: React.CSSProperties = { width: 18, height: 18, accentColor: 'hsl(var(--primary))' };
const delBtn: React.CSSProperties = { ...secondaryBtn, color: '#ef4444', borderColor: '#fecaca', fontSize: 12, padding: '6px 14px' };
const tabBar: React.CSSProperties = { display: 'flex', gap: 4, marginBottom: 28, borderBottom: '1px solid hsl(var(--border))' };
const tab = (a: boolean): React.CSSProperties => ({
  padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
  borderBottom: a ? '2px solid hsl(var(--primary))' : '2px solid transparent',
  background: 'transparent', color: a ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))', transition: 'all 150ms',
});
const textarea: React.CSSProperties = { ...inputStyle, minHeight: 100, resize: 'vertical' as any };

const DOC_TYPES = ['PRIVACY_POLICY', 'TERMS_OF_SERVICE', 'CANCELLATION_POLICY'];
const TABS = ['Guide Sections', 'Legal Documents', 'About App'] as const;
type Tab = typeof TABS[number];

const ContentDocs = () => {
  const [activeTab, setActiveTab] = useState<Tab>('Guide Sections');
  const [targetApp, setTargetApp] = useState<'USER' | 'SCHOLAR'>('USER');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const { toast, showToast } = useToast();

  /* guide fields */
  const [gTitle, setGTitle] = useState('');
  const [gIcon, setGIcon] = useState('');
  const [gColor, setGColor] = useState('#7c3aed');
  const [gTips, setGTips] = useState('');
  const [gSort, setGSort] = useState(0);
  const [gActive, setGActive] = useState(true);

  /* legal fields */
  const [lType, setLType] = useState('PRIVACY_POLICY');
  const [lContent, setLContent] = useState('');
  const [lVersion, setLVersion] = useState('1.0');
  const [lActive, setLActive] = useState(true);

  /* about fields */
  const [aMainHeading, setAMainHeading] = useState('');
  const [aHeaderSubtitle, setAHeaderSubtitle] = useState('');
  const [aCopyrightText, setACopyrightText] = useState('');
  const [aVersion, setAVersion] = useState('');
  const [aMrtsLink, setAMrtsLink] = useState('');
  const [aSections, setASections] = useState('[]');
  const [aSocialLinks, setASocialLinks] = useState('[]');

  const fetchData = useCallback(async () => {
    setLoading(true);
    if (activeTab === 'Guide Sections') {
      const items = await getGuideSections();
      setData(items);
    } else if (activeTab === 'Legal Documents') {
      const items = await getLegalDocuments(targetApp);
      setData(items);
    } else {
      // About App
      const info = await getAboutInfo(targetApp);
      if (info) {
        setAMainHeading(info.mainHeading || '');
        setAHeaderSubtitle(info.headerSubtitle || '');
        setACopyrightText(info.copyrightText || '');
        setAVersion(info.version || '');
        setAMrtsLink(info.mrtsLink || '');
        setASections(JSON.stringify(info.sections || [], null, 2));
        setASocialLinks(JSON.stringify(info.socialLinks || [], null, 2));
      } else {
        setAMainHeading('');
        setAHeaderSubtitle('');
        setACopyrightText('');
        setAVersion('');
        setAMrtsLink('');
        setASections('[]');
        setASocialLinks('[]');
      }
    }
    setLoading(false);
  }, [activeTab, targetApp]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setGTitle(''); setGIcon('📌'); setGColor('#7c3aed'); setGTips(''); setGSort(0); setGActive(true);
    setLType('PRIVACY_POLICY'); setLContent(''); setLVersion('1.0'); setLActive(true);
    setShowModal(true);
  };

  const openEdit = (item: any) => {
    setEditing(item);
    if (activeTab === 'Guide Sections') {
      setGTitle(item.title || ''); setGIcon(item.icon || ''); setGColor(item.color || '#7c3aed');
      setGTips(Array.isArray(item.tips) ? JSON.stringify(item.tips, null, 2) : '[]');
      setGSort(item.sortOrder ?? 0); setGActive(item.isActive ?? true);
    } else {
      setLType(item.type || 'PRIVACY_POLICY');
      setLContent(typeof item.content === 'object' ? JSON.stringify(item.content, null, 2) : item.content || '');
      setLVersion(item.version || '1.0'); setLActive(item.isActive ?? true);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'Guide Sections') {
        let tips: any;
        try { tips = JSON.parse(gTips); } catch { tips = gTips.split('\n').filter(Boolean); }
        const payload = { title: gTitle, icon: gIcon, color: gColor, tips, sortOrder: gSort, isActive: gActive };
        editing ? await updateGuideSection(editing.id, payload) : await createGuideSection(payload);
      } else {
        let content: any;
        try { content = JSON.parse(lContent); } catch { content = { text: lContent }; }
        const payload = { type: lType, content, version: lVersion, isActive: lActive, targetApp: editing ? editing.targetApp : targetApp };
        editing ? await updateLegalDocument(editing.id, payload) : await createLegalDocument(payload);
      }
      showToast(editing ? 'Updated successfully' : 'Created successfully');
      setShowModal(false); fetchData();
    } catch { showToast('Save failed', 'error'); }
    setSaving(false);
  };

  const handleSaveAbout = async () => {
    setSaving(true);
    try {
      let parsedSections = [];
      let parsedSocialLinks = [];
      try { parsedSections = JSON.parse(aSections); } catch { showToast('Invalid JSON for Sections', 'error'); setSaving(false); return; }
      try { parsedSocialLinks = JSON.parse(aSocialLinks); } catch { showToast('Invalid JSON for Social Links', 'error'); setSaving(false); return; }

      await updateAboutInfo({
        mainHeading: aMainHeading,
        headerSubtitle: aHeaderSubtitle,
        copyrightText: aCopyrightText,
        version: aVersion,
        mrtsLink: aMrtsLink,
        sections: parsedSections,
        socialLinks: parsedSocialLinks,
        targetApp,
      });

      showToast('About information updated successfully');
    } catch (err: any) {
      showToast('Failed to update About info', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      activeTab === 'Guide Sections' ? await deleteGuideSection(id) : await deleteLegalDocument(id);
      showToast('Deleted'); fetchData();
    } catch { showToast('Delete failed', 'error'); }
  };

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader breadcrumb="Dashboard › Content & Docs" title="Content & Legal Docs" subtitle="Manage guide sections, legal documents, and about application."
        onRefresh={fetchData} extraButtons={activeTab !== 'About App' ? <button style={primaryBtn} onClick={openCreate}>+ New</button> : null} />

      <div style={tabBar}>
        {TABS.map(t => <button key={t} style={tab(activeTab === t)} onClick={() => setActiveTab(t)}>{t}</button>)}
      </div>

      {activeTab !== 'Guide Sections' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, background: 'hsl(var(--muted)/0.05)', padding: 4, borderRadius: 10, alignSelf: 'flex-start', width: 'fit-content', border: '1px solid hsl(var(--border))' }}>
          <button
            onClick={() => setTargetApp('USER')}
            style={{
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              background: targetApp === 'USER' ? 'hsl(var(--card))' : 'transparent',
              color: targetApp === 'USER' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
              boxShadow: targetApp === 'USER' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            👤 User App
          </button>
          <button
            onClick={() => setTargetApp('SCHOLAR')}
            style={{
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              background: targetApp === 'SCHOLAR' ? 'hsl(var(--card))' : 'transparent',
              color: targetApp === 'SCHOLAR' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
              boxShadow: targetApp === 'SCHOLAR' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            🎓 Scholar App
          </button>
        </div>
      )}

      <div style={cardStyle}>
        {activeTab === 'Guide Sections' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>ICON</th><th style={thStyle}>TITLE</th><th style={thStyle}>COLOR</th><th style={thStyle}>TIPS</th><th style={thStyle}>ACTIVE</th><th style={thStyle}>ORDER</th><th style={thStyle}>ACTIONS</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
              : data.length === 0 ? <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No guide sections found.</td></tr>
              : data.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  <td style={tdStyle}><span style={{ fontSize: 20 }}>{item.icon}</span></td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{item.title}</td>
                  <td style={tdStyle}><span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 6, background: item.color, verticalAlign: 'middle' }} /> <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 6 }}>{item.color}</span></td>
                  <td style={tdStyle}>{Array.isArray(item.tips) ? `${item.tips.length} tips` : '—'}</td>
                  <td style={tdStyle}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: item.isActive ? '#f0fdf4' : '#fef2f2', color: item.isActive ? '#15803d' : '#dc2626' }}>{item.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td style={tdStyle}>{item.sortOrder}</td>
                  <td style={tdStyle}><div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...secondaryBtn, fontSize: 12, padding: '6px 14px' }} onClick={() => openEdit(item)}>Edit</button>
                    <button style={delBtn} onClick={() => handleDelete(item.id)}>Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'Legal Documents' ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>TYPE</th><th style={thStyle}>VERSION</th><th style={thStyle}>ACTIVE</th><th style={thStyle}>UPDATED</th><th style={thStyle}>ACTIONS</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
              : data.length === 0 ? <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No legal documents found.</td></tr>
              : data.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  <td style={tdStyle}><span style={badgeStyle}>{item.type?.replace(/_/g, ' ')}</span></td>
                  <td style={tdStyle}>{item.version}</td>
                  <td style={tdStyle}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: item.isActive ? '#f0fdf4' : '#fef2f2', color: item.isActive ? '#15803d' : '#dc2626' }}>{item.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td style={tdStyle}>{new Date(item.updatedAt).toLocaleDateString()}</td>
                  <td style={tdStyle}><div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...secondaryBtn, fontSize: 12, padding: '6px 14px' }} onClick={() => openEdit(item)}>Edit</button>
                    <button style={delBtn} onClick={() => handleDelete(item.id)}>Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '8px' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Edit Application "About" Information</h3>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading About Information...</div>
            ) : (
              <>
                <div style={row}>
                  <label style={lbl}>Main Heading *</label>
                  <input style={inputStyle} value={aMainHeading} onChange={e => setAMainHeading(e.target.value)} placeholder="e.g. About Divinifyy" />
                </div>
                <div style={row}>
                  <label style={lbl}>Header Subtitle *</label>
                  <input style={inputStyle} value={aHeaderSubtitle} onChange={e => setAHeaderSubtitle(e.target.value)} placeholder="e.g. Connecting devotees with scholars..." />
                </div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>App Version</label>
                    <input style={inputStyle} value={aVersion} onChange={e => setAVersion(e.target.value)} placeholder="e.g. 1.0.0" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Infitech Corp / Terms Link</label>
                    <input style={inputStyle} value={aMrtsLink} onChange={e => setAMrtsLink(e.target.value)} placeholder="e.g. https://divinifyy.com/terms" />
                  </div>
                </div>
                <div style={row}>
                  <label style={lbl}>Copyright Text *</label>
                  <input style={inputStyle} value={aCopyrightText} onChange={e => setACopyrightText(e.target.value)} placeholder="e.g. © 2026 Divinifyy Ecosystem" />
                </div>
                <div style={row}>
                  <label style={lbl}>Sections (JSON Array of {"{ title, content, icon }"})</label>
                  <textarea style={{ ...textarea, minHeight: 140 }} value={aSections} onChange={e => setASections(e.target.value)} />
                </div>
                <div style={row}>
                  <label style={lbl}>Social Links (JSON Array of {"{ platform, icon, url }"})</label>
                  <textarea style={{ ...textarea, minHeight: 120 }} value={aSocialLinks} onChange={e => setASocialLinks(e.target.value)} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                  <button style={primaryBtn} onClick={handleSaveAbout} disabled={saving}>
                    {saving ? 'Saving...' : 'Save & Publish Changes'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div style={overlay} onClick={() => setShowModal(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'hsl(var(--foreground))' }}>
              {editing ? 'Edit' : 'Create'} {activeTab === 'Guide Sections' ? 'Guide Section' : 'Legal Document'}
            </h3>

            {activeTab === 'Guide Sections' ? <>
              <div style={row}><label style={lbl}>Title *</label><input style={inputStyle} value={gTitle} onChange={e => setGTitle(e.target.value)} /></div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...row, flex: 1 }}><label style={lbl}>Icon *</label><input style={inputStyle} value={gIcon} onChange={e => setGIcon(e.target.value)} placeholder="📌" /></div>
                <div style={{ ...row, flex: 1 }}><label style={lbl}>Color *</label><input type="color" style={{ ...inputStyle, height: 42, padding: 4 }} value={gColor} onChange={e => setGColor(e.target.value)} /></div>
              </div>
              <div style={row}><label style={lbl}>Tips (JSON array or one per line)</label><textarea style={textarea} value={gTips} onChange={e => setGTips(e.target.value)} /></div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
                <div style={{ flex: 1 }}><label style={lbl}>Sort Order</label><input type="number" style={inputStyle} value={gSort} onChange={e => setGSort(Number(e.target.value))} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
                  <input type="checkbox" checked={gActive} onChange={e => setGActive(e.target.checked)} style={toggle} /><label style={{ fontSize: 13 }}>Active</label>
                </div>
              </div>
            </> : <>
              <div style={row}>
                <label style={lbl}>Type *</label>
                <select style={inputStyle} value={lType} onChange={e => setLType(e.target.value)}>
                  {DOC_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div style={row}><label style={lbl}>Content (JSON) *</label><textarea style={{ ...textarea, minHeight: 160 }} value={lContent} onChange={e => setLContent(e.target.value)} /></div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
                <div style={{ flex: 1 }}><label style={lbl}>Version</label><input style={inputStyle} value={lVersion} onChange={e => setLVersion(e.target.value)} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}>
                  <input type="checkbox" checked={lActive} onChange={e => setLActive(e.target.checked)} style={toggle} /><label style={{ fontSize: 13 }}>Active</label>
                </div>
              </div>
            </>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button style={secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentDocs;
